import { Link } from "react-router-dom";
import { Heart, CheckCircle2, ShieldCheck, Coffee, ArrowRight } from "lucide-react";

const VALUES = [
  {
    icon: CheckCircle2,
    title: "I only recommend what I use",
    body: "Every service on this site is one I've personally used for at least a month. If it doesn't earn me real money — or it's a hassle to set up — it doesn't get a listing.",
  },
  {
    icon: ShieldCheck,
    title: "Transparent about referral links",
    body: "Most outbound links here are referral links. If you sign up through them, I earn a small commission — often you get a joining bonus too. Either way, my opinion isn't for sale.",
  },
  {
    icon: Coffee,
    title: "Written for real households",
    body: "No get-rich-quick, no crypto side-hustles, no MLMs. Just practical UK tools that quietly earn £5–£50 a month with almost no effort once they're set up.",
  },
];

export default function About() {
  return (
    <main data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mds-container pt-16 pb-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="mds-badge mds-badge-primary"><Heart size={14} /> About the site</span>
            <h1 className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-4">
              Hi, I'm Matt. This is my honest UK money-saving stack.
            </h1>
            <p className="text-[#4A5D54] mt-5 max-w-xl leading-relaxed text-lg">
              Matt's Daily Savers started as a spreadsheet on my kitchen table — a note of every cashback site, reward app, card tool and survey panel that actually pays me something. This site is that spreadsheet, tidied up.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/showcase" className="mds-btn mds-btn-primary" data-testid="about-cta-showcase">
                See my £220/mo breakdown <ArrowRight size={16} />
              </Link>
              <Link to="/directory" className="mds-btn mds-btn-secondary" data-testid="about-cta-directory">
                Browse the directory
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-[#E5E3DB] shadow-md">
              <img
                src="https://images.unsplash.com/photo-1566753323558-f4e0952af115?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxmcmllbmRseSUyMG1hbiUyMHBvcnRyYWl0JTIwc21pbGluZyUyMG5hdHVyYWx8ZW58MHx8fHwxNzgzMzQ2ODkyfDA&ixlib=rb-4.1.0&q=85"
                alt="Matt"
                className="w-full h-[420px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="mds-container py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 mds-prose text-base">
          <h2 className="font-heading font-semibold text-3xl text-[#1C2321] mb-4">The story</h2>
          <p>
            I've been quietly stacking cashback sites, reward apps and survey panels since 2015. Nothing fancy — just a habit of always checking Quidco or TopCashback before an online shop, buying discounted gift cards through JamDoughnut for the high street, using Curve at the till so my payments always come out of the right card, and running Sprive in the background to overpay my mortgage.
          </p>
          <p>
            Add it all up and the stack pays me around <strong>£220 a month</strong> — real cash into a real bank account. Not enough to change your life, but enough to cover a weekly food shop, or the mortgage overpayment, or a nice family holiday every year.
          </p>
          <p>
            Friends and family kept asking which apps to use and in what order. Rather than repeat myself, I built this site. Every service has a plain-English write-up: how it works, how I use it, roughly how much it pays me, and whether it's worth your time.
          </p>

          <h2 className="font-heading font-semibold text-3xl text-[#1C2321] mt-10 mb-4">How the site is funded</h2>
          <p>
            Most outbound links here are <strong>referral / affiliate links</strong>. If you click one and sign up, the third-party service pays me a small commission or bonus — at no additional cost to you (and often with a joining bonus for you too). That commission is what keeps this site running.
          </p>
          <p>
            To be clear: I don't accept payment to feature a service, and no third party sees this site before it's published. If a link is a referral link, it's flagged on the page and again at the bottom of every page. If a service stops working for me, I remove it.
          </p>
          <p>
            The site also shows Google AdSense ads. Ads help cover hosting and email costs. You can read more about how they work in our <Link to="/privacy" className="mds-link">Privacy Policy</Link>.
          </p>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          {VALUES.map((v, i) => (
            <div key={i} className="mds-card" data-testid={`about-value-${i}`}>
              <span className="w-11 h-11 rounded-xl grid place-items-center mb-4" style={{ background: "rgba(132,169,140,0.15)", color: "#6B8F73" }}>
                <v.icon size={22} strokeWidth={1.6} />
              </span>
              <h3 className="font-heading font-semibold text-lg">{v.title}</h3>
              <p className="text-sm text-[#4A5D54] mt-2 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </aside>
      </section>

      {/* CTA */}
      <section className="mds-container py-12 pb-16">
        <div className="rounded-3xl p-8 sm:p-12" style={{ background: "linear-gradient(135deg, #84A98C 0%, #6B8F73 100%)" }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <h3 className="font-heading font-semibold text-white text-2xl sm:text-3xl leading-tight">
                Got a question, or a service I should review?
              </h3>
              <p className="text-white/85 mt-3 max-w-xl">
                Drop me a note — I read every message and try to reply within a couple of days.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/contact" className="mds-btn bg-white text-[#1C2321] hover:bg-[#F0EFEA]" data-testid="about-cta-contact">
                Say hi <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
